import { useCallback, useEffect, useRef } from 'react'

import { useTrackEvent } from '@percent/education-hosted-form/analytics/useTrackEvent'
import { EducationHostedFormSteps } from '@percent/education-hosted-form/app/EducationHostedForm/EducationHostedForm/EducationHostedForm.types'

// Only track interactions slower than this (ms); ignore fast ones users don't feel as lag.
// 40 is the web-vitals library's default.
const INTERACTION_DURATION_THRESHOLD_MS = 40

/**
 * The INP value to report for one step: the worst of its interactions.
 *
 * `durations` = the worst latency (ms) per interaction during the step, or `[]`
 * if it had none. Returns `null` when there's nothing to report.
 */
export function computeStepInp(durations: number[]): number | null {
  if (durations.length === 0) return null

  return Math.max(...durations)
}

// The browser puts `interactionId` on event entries, but TypeScript's DOM types haven't
// caught up — so declare it here. (`duration` is already on the base PerformanceEntry.)
interface EventTimingEntry extends PerformanceEntry {
  readonly interactionId: number
}

// We only observe `event` entries — check for them instead of casting.
const isEventTiming = (entry: PerformanceEntry): entry is EventTimingEntry => entry.entryType === 'event'

/**
 * Measures INP (Interaction to Next Paint) per form step.
 *
 * Web vitals only give one INP for the whole page, which is useless here: every step
 * is a client-side `pageId` swap in a single page load. So we watch interaction
 * timings, take the worst per step, and send one `education_form_step_inp` event per
 * step, keyed off `formStep` (not DOM selectors). Chromium-only; does nothing elsewhere.
 *
 * Fires one event per step *visit*, not deduped like `education_form_step_viewed` —
 * going back into a step and leaving again reports it again. So when querying, take p75
 * across visits (the Core Web Vitals convention), not any single event's `inp_ms`.
 */
export function usePerStepINP(currentStep: EducationHostedFormSteps) {
  const trackEvent = useTrackEvent()
  const stepRef = useRef(currentStep)
  // interactionId -> worst latency (ms) for the current step
  const interactions = useRef<Map<number, number>>(new Map())
  const observerRef = useRef<PerformanceObserver | null>(null)

  // Record each interaction's worst latency. Used by both the observer and the drain below.
  const ingestEntries = useCallback((entries: PerformanceEntry[]) => {
    for (const entry of entries) {
      // Count only real interactions (interactionId 0 = things like mousemove).
      if (isEventTiming(entry) && entry.interactionId) {
        const worst = interactions.current.get(entry.interactionId) ?? 0

        if (entry.duration > worst) {
          interactions.current.set(entry.interactionId, entry.duration)
        }
      }
    }
  }, [])

  const flush = useCallback(
    (step: EducationHostedFormSteps) => {
      const inp = computeStepInp([...interactions.current.values()])
      interactions.current.clear()

      if (inp !== null) {
        trackEvent('education_form_step_inp', { step, inp_ms: Math.round(inp) })
      }
    },
    [trackEvent]
  )

  // On step change, report the step we're leaving.
  useEffect(() => {
    if (stepRef.current !== currentStep) {
      // Fix for interactions leaking onto the *next* step: the observer delivers entries
      // asynchronously, so pull any still-pending ones in now so they count for the step
      // being left. (The click that causes the switch is the exception — it lands on the next step.)
      ingestEntries(observerRef.current?.takeRecords() ?? [])

      flush(stepRef.current)
      stepRef.current = currentStep
    }
  }, [currentStep, flush, ingestEntries])

  useEffect(() => {
    // Do nothing if Event Timing isn't supported (also covers jsdom/SSR). Check `typeof`
    // first — reading a global that doesn't exist would throw.
    if (typeof PerformanceObserver === 'undefined' || !PerformanceObserver.supportedEntryTypes?.includes('event')) {
      return () => undefined
    }

    const observer = new PerformanceObserver(list => ingestEntries(list.getEntries()))
    observerRef.current = observer

    observer.observe({
      type: 'event',
      buffered: true,
      durationThreshold: INTERACTION_DURATION_THRESHOLD_MS
    } as PerformanceObserverInit)

    // The last step never changes, so also report when the page is hidden or closed.
    const handleHide = () => {
      if (document.visibilityState === 'hidden') {
        flush(stepRef.current)
      }
    }
    const handlePageHide = () => flush(stepRef.current)

    document.addEventListener('visibilitychange', handleHide)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      observer.disconnect()
      observerRef.current = null
      document.removeEventListener('visibilitychange', handleHide)
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [flush, ingestEntries])
}
